import z from "zod";
import { useQuery } from "react-query";

import { publicApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";

export const ACTION_PLAN_MODES = ["simplified", "advanced"] as const;
export type ActionPlanModes = (typeof ACTION_PLAN_MODES)[number];

const actionPlanKeys = [
    "actionPlan-scenario-without",
    "actionPlan-scenario-with",
    "actionPlan-mode",
    "actionPlan-targetCampaign",
    "actionPlan-allowResponsibleSelection",
] as const;
// type ActionPlanKeys = (typeof actionPlanKeys)[number];

const featureSwitchKeys = [
    "FS-cms-enabled",
    "FS-strategy",
    "FS-documentary-space",
    "FS-news-feed",
    "FS-app-integration",

    "FS-entity-companies",
    "FS-entity-products",
    "FS-entity-intermediate-products",
    "FS-entity-ingredients",
    "FS-manage-my-ingredients",
    "FS-entity-packagings",
    "FS-manage-my-packagings",

    "FS-diagnostic",

    "FS-company-score-details-comparison",
    "FS-score-details-evolution",
    "FS-actionPlan",
    "FS-ssq-export-excel",
    "FS-collective-pdf-export",
    "FS-network-tracking",

    "FS-company-score-details-distribution",
    "FS-product-score-details-distribution",
    "FS-ingredient-score-details-distribution",
    "FS-packaging-score-details-distribution",

    "FS-product-value-chain",
    "FS-product-distribution",
    "FS-manage-my-distribution",
    "FS-product-consumption",
    "FS-product-transformation",
    "FS-manage-my-transformation",
    "FS-product-transport",
    "FS-manage-my-transport",
    "FS-transport-stage-product-to-storage",
    "FS-transport-stage-ingredient-to-processing-site",
    "FS-transport-stage-packaging-to-processing-site",
    "FS-transport-stage-retail-to-consumer",
    "FS-transport-stage-storage-to-retail",
    "FS-product-ux-v2",
    "FS-open-food-facts-search",

    "FS-legal-requirement",

    "FS-icv-management",
    "FS-platform-ingredient-references",

    "dx-ux-testing",
    "FS-company-settings",
    "FS-payment-enabled",
    "FS-consent-governance",
] as const;

export const applicationParameterKeys = [
    ...featureSwitchKeys,
    ...actionPlanKeys,
    "logo",
    "logoSquare",
    "freshdeskWidgetId",
    "amplitudeApiKey",
    "gtmIdGlobal",
    "gtmIdClient",
    "front_cover_pdf",
    "tabIcon",
    "tabTitle",
    "theme",
    "supersetDashboardUuid",
    "packagingsSsqId",
    "sso-connector-path",
    "sso-connector-label",
    "sso-connector-logo",
    "sso-login-mode",
] as const;

export type ApplicationParameterKeys = (typeof applicationParameterKeys)[number];

export const isAppParamKey = (key: string): key is ApplicationParameterKeys => {
    return applicationParameterKeys.includes(key as ApplicationParameterKeys);
};

// It is string | undefined because we can't promise the user that the key is defined in the backend.
type AppParamsRecord = Record<ApplicationParameterKeys, string | undefined>;

export const appParamCacheKey = "applicationParameterKey" as const;

export const useApplicationParameters = () => {
    const query = useQuery({
        cacheTime: 0,
        staleTime: Infinity,
        queryKey: [appParamCacheKey],
        queryFn: async () => {
            return await publicApiCall.get(`/api/application_parameters`);
        },
        retry: (failureCount, error: any) => {
            // Retry only on network errors, not on client errors
            if (error?.response?.status && error.response.status < 500) {
                return false;
            }
            return failureCount < 3;
        },
        onError: (error) => {
            console.error("Failed to fetch application parameters:", error);
        },
        select: (request) => {
            // Validate that we have a valid response
            if (!request?.data) {
                console.error("No data received from application parameters API");
                const defaultParams: AppParamsRecord = applicationParameterKeys.reduce((acc, item) => {
                    acc[item] = undefined;
                    return acc;
                }, {} as AppParamsRecord);
                return { appParamRecord: defaultParams, fullRecord: {} };
            }

            const appParams = request.data["hydra:member"] ?? [];

            // Validate that appParams is an array
            if (!Array.isArray(appParams)) {
                console.error("Expected hydra:member to be an array, got:", typeof appParams);
                // Return empty result if the API response is malformed
                const defaultParams: AppParamsRecord = applicationParameterKeys.reduce((acc, item) => {
                    acc[item] = undefined;
                    return acc;
                }, {} as AppParamsRecord);
                return { appParamRecord: defaultParams, fullRecord: {} };
            }

            const data = zodParseWithLog(appParams, ApplicationParametersZodSchema.array(), "applicationParams");

            const defaultParams: AppParamsRecord = applicationParameterKeys.reduce((acc, item) => {
                // This put to `undefined` every key defined in applicationParameterKeys above;
                acc[item] = undefined;
                return acc;
            }, {} as AppParamsRecord);

            const appParamRecord = data.reduce<AppParamsRecord>((acc, item) => {
                if (isAppParamKey(item.key)) {
                    // If key in AppParam isn't declared in front, it's just ignored
                    acc[item.key] = item.value;
                }
                return acc;
            }, defaultParams);

            // Créer un record complet avec toutes les clés (y compris dynamiques)
            const fullRecord = data.reduce<Record<string, string | undefined>>((acc, item) => {
                acc[item.key] = item.value;
                return acc;
            }, {});

            return { appParamRecord, fullRecord };
        },
    });

    const isFeatureActivated = (key: string): boolean => {
        const raw = query.data?.fullRecord?.[key] as unknown;
        if (typeof raw === "boolean") return raw === true;
        if (typeof raw === "number") return raw === 1;
        if (typeof raw === "string") {
            const n = raw.trim().toLowerCase();
            return n === "true" || n === "1" || n === "yes" || n === "on";
        }
        return false;
    };

    const isActionPlanSimplified = (): boolean => {
        if (query.data?.appParamRecord?.["actionPlan-mode"] === "simplified") {
            return true;
        }

        return false;
    };

    return {
        applicationParameters: query.data?.appParamRecord,
        fullApplicationParameters: query.data?.fullRecord,
        isFeatureActivated,
        isActionPlanSimplified,
        ...query,
    };
};

export type ApplicationParameter = {
    "@id": IRI;
    key: string;
    value: string;
};

export const ApplicationParametersZodSchema: z.ZodSchema<ApplicationParameter> = z.object({
    "@id": z.string(),
    key: z.string(),
    value: z.string(),
});
